import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/vue';
import { api } from '@/services/api';
import type { User, UserRole } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const isInitialized = ref(false);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const loginErrorCode = ref<string | null>(null);

  const isAuthenticated = computed(() => !!user.value);
  const isAdmin = computed(() =>
    user.value?.groups.includes('admin') || user.value?.groups.includes('superadmin')
  );
  const isSuperAdmin = computed(() => user.value?.groups.includes('superadmin'));
  const memberId = computed(() => user.value?.memberId);

  async function checkAuth() {
    isLoading.value = true;
    error.value = null;

    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      if (currentUser && session.tokens) {
        const idToken = session.tokens.idToken;
        const payload = idToken?.payload;

        user.value = {
          id: currentUser.userId,
          email: payload?.email as string || '',
          memberId: payload?.['custom:memberId']
            ? parseInt(payload['custom:memberId'] as string, 10)
            : undefined,
          role: (payload?.['custom:role'] as UserRole) || 'member',
          groups: (payload?.['cognito:groups'] as string[]) || [],
        };

        // Identify user in PostHog (memberId as stable ID; email/name as person properties so
        // the session replay list shows a human-readable label instead of the distinct ID —
        // PostHog's "person display name" project setting picks name, falling back to email).
        let memberName: string | undefined;
        if (user.value.memberId) {
          try {
            const { member } = await api.getMember(user.value.memberId);
            memberName = `${member.personal.firstName} ${member.personal.lastName}`.trim();
          } catch {
            // Non-fatal — identify still proceeds with email only.
          }
        }

        posthog.identify(String(user.value.memberId ?? currentUser.userId), {
          email: user.value.email,
          ...(memberName ? { name: memberName } : {}),
          role: user.value.role,
          is_admin: user.value.groups.includes('admin') || user.value.groups.includes('superadmin'),
        });

        // Set Sentry user context (ID only — no PII)
        Sentry.setUser({
          id: String(user.value.memberId ?? currentUser.userId),
          role: user.value.role,
        });
      }
    } catch {
      user.value = null;
    } finally {
      isInitialized.value = true;
      isLoading.value = false;
    }
  }

  async function login(email: string, password: string, entryPoint?: string) {
    isLoading.value = true;
    error.value = null;
    loginErrorCode.value = null;

    try {
      // Cognito usernames are created lowercase (see backend auth/handler.ts
      // normalizedEmail) and this pool's username matching is case-sensitive
      // (signInCaseSensitive defaults to true) — normalize here so a mobile
      // keyboard auto-capitalizing the email field doesn't break sign-in.
      const result = await signIn({
        username: email.trim().toLowerCase(),
        password,
      });

      if (result.isSignedIn) {
        await checkAuth();
        posthog.capture('login_succeeded', { role: user.value?.role, entry_point: entryPoint });
        return true;
      }

      error.value = 'Login failed';
      posthog.capture('login_failed', { error_type: 'sign_in_incomplete', password_length: password.length });
      return false;
    } catch (err) {
      const cognitoCode = (err as any)?.name || '';
      const friendlyMessage = cognitoCode === 'NotAuthorizedException' || cognitoCode === 'UserNotFoundException'
        ? 'Incorrect email or password.'
        : cognitoCode === 'UserNotConfirmedException'
          ? 'Your account has not been confirmed. Check your email for an activation link.'
          : cognitoCode === 'PasswordResetRequiredException'
            ? 'A password reset is required. Use "Forgot password" to set a new one.'
            : cognitoCode === 'LimitExceededException' || cognitoCode === 'TooManyRequestsException'
              ? 'Too many attempts. Please wait a moment and try again.'
              : 'Sign-in failed. Please try again.';
      // Don't report expected user errors to Sentry — wrong password, unknown user, rate limit
      const expectedErrors = ['NotAuthorizedException', 'UserNotFoundException', 'LimitExceededException', 'TooManyRequestsException'];
      if (!expectedErrors.includes(cognitoCode)) {
        Sentry.captureException(err, { extra: { cognitoCode, endpoint: 'login' } });
      }
      error.value = friendlyMessage;
      loginErrorCode.value = cognitoCode;
      // password_length is a count only — never the password content — to help
      // distinguish e.g. mobile autofill/whitespace issues from a genuinely
      // wrong password, without capturing anything sensitive.
      posthog.capture('login_failed', { error_type: cognitoCode || 'unknown', password_length: password.length });
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    isLoading.value = true;

    try {
      posthog.capture('logout');
      await signOut();
      user.value = null;
      posthog.reset(); // disassociate the user identity
      Sentry.setUser(null); // clear user context
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      isLoading.value = false;
    }
  }

  function clearError() {
    error.value = null;
    loginErrorCode.value = null;
  }

  return {
    user,
    isInitialized,
    isLoading,
    error,
    loginErrorCode,
    isAuthenticated,
    isAdmin,
    isSuperAdmin,
    memberId,
    checkAuth,
    login,
    logout,
    clearError,
  };
});
